import { useLocalStorage } from "@vueuse/core"

import type { DashboardCardIdentifier, DashboardCardPayload, DashboardCardPayloads } from "@/modules/dashboard/types"

export type CacheContext = {
  appId: number
  userId: number
}

type CardCacheEntry<Payload extends DashboardCardPayload = DashboardCardPayload> = {
  expiresAt: number
  payload: Payload
}

type DashboardCardCache = Record<string, CardCacheEntry>

const dashboardCardCache = useLocalStorage<DashboardCardCache>(
  "dashboard-card-cache:v3",
  {},
  {
    listenToStorageChanges: false,
    onError: (storageError) => {
      throw storageError
    },
    writeDefaults: false,
  },
)

function createCardCacheEntryKey(context: CacheContext, cardId: DashboardCardIdentifier): string {
  return `${context.appId}:${context.userId}:${cardId}`
}

export function getCachedCard<Identifier extends DashboardCardIdentifier>(context: CacheContext, cardId: Identifier): DashboardCardPayloads[Identifier] | undefined {
  const cacheEntryKey = createCardCacheEntryKey(context, cardId)
  const cacheEntry = dashboardCardCache.value[cacheEntryKey] as CardCacheEntry<DashboardCardPayloads[Identifier]> | undefined

  if (cacheEntry === undefined) {
    return undefined
  }

  if (Date.now() >= cacheEntry.expiresAt) {
    delete dashboardCardCache.value[cacheEntryKey]
    return undefined
  }

  return cacheEntry.payload
}

export function cacheCard<Identifier extends DashboardCardIdentifier>(context: CacheContext, cardId: Identifier, payload: DashboardCardPayloads[Identifier]): void {
  dashboardCardCache.value[createCardCacheEntryKey(context, cardId)] = {
    expiresAt: Date.now() + payload.cache_retention_seconds * 1000,
    payload,
  }
}

export function removeCachedCard(context: CacheContext, cardId: DashboardCardIdentifier): void {
  delete dashboardCardCache.value[createCardCacheEntryKey(context, cardId)]
}

export function clearCardCache(): void {
  dashboardCardCache.value = {}
}
